import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import UserMenu from './UserMenu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectsPipeline = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pipelineData, setPipelineData] = useState(null);
  const [totals, setTotals] = useState(null);
  const [years, setYears] = useState([]);
  const [managers, setManagers] = useState([]);
  const [selectedManager, setSelectedManager] = useState('all');
  const [loading, setLoading] = useState(true);
  const [draggedProject, setDraggedProject] = useState(null);
  const [showLostProjects, setShowLostProjects] = useState(false); // Hide lost projects by default in pipeline
  const containerRef = useRef(null);

  useEffect(() => {
    loadManagers();
    loadPipelineData();
  }, [selectedManager]);

  const loadManagers = async () => {
    try {
      // Only admin and lead can see manager filter
      if (user?.role === 'admin' || user?.role === 'lead') {
        const response = await axios.get(`${API}/managers`);
        setManagers(response.data.managers || []);
      }
    } catch (error) {
      console.error('Failed to load managers:', error);
    }
  };

  const loadPipelineData = async () => {
    try {
      const params = selectedManager !== 'all' ? { manager_id: selectedManager } : {};
      const response = await axios.get(`${API}/projects/pipeline`, { params });
      
      setPipelineData(response.data.pipeline);
      setTotals(response.data.totals);
      setYears(response.data.years);
    } catch (error) {
      console.error('Failed to load pipeline data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '0 ₸';
    return new Intl.NumberFormat('ru-RU').format(amount) + ' ₸';
  };

  const getProjectCardColor = (project) => {
    // Get color based on first flag
    const flags = project.flags || [];
    if (flags.length === 0) return 'bg-white';
    
    const firstFlag = flags[0];
    const flagColors = {
      hot: 'bg-orange-50 border-orange-200',
      paused: 'bg-gray-50 border-gray-200',
      risk: 'bg-red-50 border-red-200',
      waiting_construction: 'bg-blue-50 border-blue-200',
      waiting_funding: 'bg-cyan-50 border-cyan-200'
    };
    
    return flagColors[firstFlag] || 'bg-white';
  };

  const handleDragStart = (e, project) => {
    setDraggedProject(project);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, year, quarter) => {
    e.preventDefault();
    
    if (!draggedProject) return;

    // Check if already in this quarter/year
    if (draggedProject.build_start_year === year && draggedProject.quarter === quarter) {
      setDraggedProject(null);
      return;
    }

    try {
      const updateData = {
        build_start_year: year || null,
        quarter: quarter || null
      };

      const response = await axios.put(`${API}/projects/${draggedProject.id}`, updateData);
      
      // Reload pipeline data
      await loadPipelineData();
    } catch (error) {
      console.error('Failed to update project:', error);
    }
    
    setDraggedProject(null);
  };

  const ProjectCard = ({ project }) => {
    const isLostProject = project.stage === 'lost';
    
    return (
      <div
        draggable
        onDragStart={(e) => handleDragStart(e, project)}
        className={`p-3 rounded-lg border-2 cursor-move hover:shadow-md transition-shadow mb-2 ${
          isLostProject 
            ? 'bg-red-100 border-red-400 opacity-75' + (showLostProjects ? '' : ' hidden') 
            : getProjectCardColor(project)
        }`}
        onClick={() => navigate(`/project/${project.id}`)}
      >
        <div className={`font-semibold text-sm mb-1 line-clamp-2 ${isLostProject ? 'text-red-800' : 'text-gray-900'}`}>
          {project.name} {isLostProject && '❌'}
        </div>
        <div className={`text-xs font-medium ${isLostProject ? 'text-red-700' : 'text-green-700'}`}>
          {formatCurrency(project.budget_amount)}
        </div>
      </div>
    );
  };

  const QuarterColumn = ({ year, quarter, projects, total }) => {
    return (
      <div
        className="flex-shrink-0 w-64 bg-gray-50 rounded-lg p-4"
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, year, quarter)}
      >
        <div className="mb-3 pb-2 border-b border-gray-300">
          <div className="font-bold text-gray-900 text-center">{quarter}</div>
          <div className="text-xs text-gray-600 text-center mt-1">
            {projects.length} {projects.length === 1 ? 'проект' : 'проектов'}
          </div>
        </div>
        
        <div className="space-y-2 mb-3" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {projects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </div>
        
        <div className="pt-2 border-t border-gray-300">
          <div className="text-xs text-gray-600">Итого:</div>
          <div className="font-semibold text-sm text-blue-700">
            {formatCurrency(total)}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-lg shadow-sm border-b border-white/20 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20">
            <div className="flex items-center">
              <div className="flex items-center min-w-0">
                <img 
                  src="/profland-logo-new.jpg" 
                  alt="Profland CRM" 
                  className="h-10 w-auto mr-4 flex-shrink-0"
                />
                <h1 className="text-xl font-semibold text-gray-900 whitespace-nowrap">Profland CRM</h1>
              </div>
              <div className="hidden md:ml-10 md:flex md:space-x-8">
                <Link to="/dashboard" className="nav-link">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                  </svg>
                  Дашборд
                </Link>
                <Link to="/kanban" className="nav-link">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                  </svg>
                  Канбан
                </Link>
                <Link to="/projects" className="nav-link">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                  Список
                </Link>
                <Link to="/map" className="nav-link">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  Карта
                </Link>
                <Link to="/pipeline" className="nav-link active">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  Pipeline
                </Link>
              </div>
            </div>
            <div className="flex items-center">
              <UserMenu />
            </div>
          </div>
        </div>
      </nav>

      {/* Filter Section */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-gray-900">Pipeline проектов</h2>
            
            {/* Manager Filter - only for admin and lead */}
            {(user?.role === 'admin' || user?.role === 'lead') && (
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Менеджер:</label>
                <select
                  value={selectedManager}
                  onChange={(e) => setSelectedManager(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">Все менеджеры</option>
                  <option value="">Неназначенные</option>
                  {managers.map((manager) => (
                    <option key={manager.id} value={manager.id}>
                      {manager.name} ({manager.role})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          
          {/* Lost Projects Toggle */}
          <button
            onClick={() => setShowLostProjects(!showLostProjects)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              showLostProjects 
                ? 'bg-red-100 text-red-800 border-2 border-red-600' 
                : 'bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-600'
            }`}
            title={showLostProjects ? 'Скрыть проигранные из pipeline' : 'Показать проигранные в pipeline'}
          >
            {showLostProjects ? '🙈 Скрыть проигранные' : '👁️ Показать проигранные'}
          </button>
        </div>
      </div>

      {/* Pipeline Timeline */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden">
        <div ref={containerRef} className="flex gap-4 p-6 h-full" style={{ minWidth: 'max-content' }}>
          {/* Unassigned Column */}
          <div
            className="flex-shrink-0 w-64 bg-yellow-50 rounded-lg p-4 border-2 border-yellow-200"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, null, null)}
          >
            <div className="mb-3 pb-2 border-b border-yellow-300">
              <div className="font-bold text-gray-900 text-center">Неразобранные</div>
              <div className="text-xs text-gray-600 text-center mt-1">
                {pipelineData?.unassigned?.length || 0} проектов
              </div>
            </div>
            
            <div className="space-y-2 mb-3" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {pipelineData?.unassigned?.map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))}
            </div>
            
            <div className="pt-2 border-t border-yellow-300">
              <div className="text-xs text-gray-600">Итого:</div>
              <div className="font-semibold text-sm text-blue-700">
                {formatCurrency(totals?.unassigned || 0)}
              </div>
            </div>
          </div>

          {/* Year/Quarter Columns */}
          {years.map((year) => (
            <div key={year} className="flex gap-4">
              {/* Year Header */}
              <div className="flex-shrink-0 w-64 bg-blue-100 rounded-lg p-4 border-2 border-blue-300 flex items-center justify-center">
                <h3 className="text-2xl font-bold text-blue-900">{year}</h3>
              </div>
              
              {/* Quarters */}
              {['Q1', 'Q2', 'Q3', 'Q4'].map((quarter) => (
                <QuarterColumn
                  key={`${year}-${quarter}`}
                  year={year}
                  quarter={quarter}
                  projects={pipelineData?.timeline?.[year]?.[quarter] || []}
                  total={totals?.timeline?.[year]?.[quarter] || 0}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white border-t border-gray-200 px-6 py-4">
        <div className="flex items-center gap-6">
          <span className="text-sm font-semibold text-gray-700">Легенда:</span>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-orange-50 border-2 border-orange-200 rounded"></div>
            <span className="text-sm text-gray-700">🔥 Горячий</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-50 border-2 border-red-200 rounded"></div>
            <span className="text-sm text-gray-700">⚠️ Риск</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gray-50 border-2 border-gray-200 rounded"></div>
            <span className="text-sm text-gray-700">⏸️ Пауза</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-50 border-2 border-blue-200 rounded"></div>
            <span className="text-sm text-gray-700">🏗️ Ожидание строительства</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-cyan-50 border-2 border-cyan-200 rounded"></div>
            <span className="text-sm text-gray-700">💰 Ждут финансирования</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-white border-2 border-gray-300 rounded"></div>
            <span className="text-sm text-gray-700">Без флагов</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectsPipeline;
